import { MemberType } from '@/shared/interfaces/crm-marketing-list.interface';
import { Auth } from '../auth.service';
import { EEntity } from '../dynamics/enums/entity.enum';
import { getPluralEntityName } from '../dynamics/helpers/get-plural-entity-name.helper';
import { logger } from '../logger.service';
import DynamicsWebApi from 'dynamics-web-api';
import { extendQueryEntityList } from './utils/extend-query-entity-list';
import { UnionEntityApiType } from '../dynamics/interfaces/api/entity.interface';
import { MAIN } from '@/configs/main.config';
import { getDomain } from '@/shared/helpers/get-domain.helper';
import { IPublicClientApplication } from '@azure/msal-browser';

export class WebApi {
  private static dynamicsWebApi: DynamicsWebApi;
  private static msalInstance: IPublicClientApplication;

  private static async acquireDynamicsToken(): Promise<string> {
    if (!WebApi.msalInstance) {
      throw new Error('MSAL instance not initialized');
    }

    const domain = getDomain();
    const crmUrl = `https://${domain}`;
    const dynamicsScope = `${crmUrl}/.default`;

    try {
      const accounts = WebApi.msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error('No accounts found');
      }

      const account = accounts[0];
      WebApi.msalInstance.setActiveAccount(account);

      const request = {
        scopes: [dynamicsScope],
        account: account,
      };

      try {
        const response = await WebApi.msalInstance.acquireTokenSilent(request);
        logger.log('[WebApi] Token acquired silently');
        return response.accessToken;
      } catch {
        logger.warn('[WebApi] Silent token acquisition failed, trying popup');
        const popupResponse = await WebApi.msalInstance.acquireTokenPopup({
          scopes: [dynamicsScope],
        });
        logger.log('[WebApi] Token acquired via popup');
        return popupResponse.accessToken;
      }
    } catch (error) {
      logger.critical('[WebApi] Failed to acquire Dynamics token:', error);
      throw error;
    }
  }

  public static setMSALInstance(instance: IPublicClientApplication): void {
    WebApi.msalInstance = instance;
  }

  public static async init(): Promise<void> {
    const domain = getDomain();
    logger.log('[WebApi] domain for webApi is', domain);

    WebApi.dynamicsWebApi = new DynamicsWebApi({
      webApiUrl: `https://${domain}${MAIN.web_api_path}`,
      onTokenRefresh: async (cb) => {
        try {
          logger.log('[WebApi] Refresh new token');
          const token = await WebApi.acquireDynamicsToken();
          cb(token);
        } catch (error) {
          logger.error('[WebApi] Refresh failed:', error);
          const fallbackToken = Auth.getDynJwtToken();
          if (fallbackToken) {
            cb(fallbackToken);
          } else {
            throw error;
          }
        }
      },
    });
  }

  public static async getEntitiesForDynamicList(
    type: MemberType,
    query: string,
  ): Promise<UnionEntityApiType[]> {
    try {
      const collection = getPluralEntityName(type as EEntity);
      const extendedQuery = extendQueryEntityList(type, query);

      const { value } = await this.dynamicsWebApi.executeFetchXmlAll(
        collection,
        extendedQuery,
      );

      return value;
    } catch (err) {
      logger.error(err as string);
    }
  }
}
